import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  EQUATION_SYMBOLS,
  getSymbolsByCategory,
  renderLatex,
  type SymbolCategory,
  type SymbolButton,
} from "@/lib/katex-config";
import "katex/dist/katex.min.css";
import { testIds } from "@/testIds";
import { symbolsEmptyState } from "./symbolsEmptyState";

interface FormulaSymbolToolbarProps {
  onInsert: (latex: string) => void;
  /** Set the search from outside (paste-to-fix's "Look up ℵ"); a new `seq`
   *  re-applies the same text. */
  query?: { text: string; seq: number } | null;
}

// Structure templates — always visible at top
const STRUCTURES: { label: string; latex: string; display: string; tooltip: string }[] = [
  { label: "a/b", latex: "\\frac{▢}{▢}", display: "\\frac{a}{b}", tooltip: "Fraction" },
  { label: "√", latex: "\\sqrt{▢}", display: "\\sqrt{x}", tooltip: "Square root" },
  { label: "ⁿ√", latex: "\\sqrt[▢]{▢}", display: "\\sqrt[n]{x}", tooltip: "Nth root" },
  { label: "xⁿ", latex: "▢^{▢}", display: "x^{n}", tooltip: "Power" },
  { label: "xₙ", latex: "▢_{▢}", display: "x_{n}", tooltip: "Subscript" },
  { label: "∑", latex: "\\sum_{▢}^{▢}", display: "\\sum_{i}^{n}", tooltip: "Sum" },
  { label: "∫", latex: "\\int_{▢}^{▢}", display: "\\int_{a}^{b}", tooltip: "Integral" },
  { label: "lim", latex: "\\lim_{▢ \\to ▢}", display: "\\lim_{x\\to a}", tooltip: "Limit" },
  { label: "∏", latex: "\\prod_{▢}^{▢}", display: "\\prod_{i}^{n}", tooltip: "Product" },
  { label: "()", latex: "\\left( ▢ \\right)", display: "\\left(x\\right)", tooltip: "Parentheses" },
  { label: "[]", latex: "\\left[ ▢ \\right]", display: "\\left[x\\right]", tooltip: "Brackets" },
  { label: "||", latex: "\\left| ▢ \\right|", display: "\\left|x\\right|", tooltip: "Absolute" },
  { label: "M", latex: "\\begin{pmatrix} ▢ & ▢ \\\\ ▢ & ▢ \\end{pmatrix}", display: "\\begin{pmatrix}a&b\\\\c&d\\end{pmatrix}", tooltip: "Matrix" },
  { label: "{", latex: "\\begin{cases} ▢ & \\text{if } ▢ \\\\ ▢ & \\text{if } ▢ \\end{cases}", display: "\\begin{cases}a\\\\b\\end{cases}", tooltip: "Cases" },
  { label: "C", latex: "\\binom{▢}{▢}", display: "\\binom{n}{k}", tooltip: "Binomial" },
];

// Accent templates
const ACCENTS: { label: string; latex: string; display: string; tooltip: string }[] = [
  { label: "hat", latex: "\\hat{▢}", display: "\\hat{a}", tooltip: "Hat" },
  { label: "bar", latex: "\\bar{▢}", display: "\\bar{a}", tooltip: "Bar / mean" },
  { label: "vec", latex: "\\vec{▢}", display: "\\vec{a}", tooltip: "Vector arrow" },
  { label: "dot", latex: "\\dot{▢}", display: "\\dot{a}", tooltip: "Dot (time derivative)" },
  { label: "ddot", latex: "\\ddot{▢}", display: "\\ddot{a}", tooltip: "Double dot" },
  { label: "tilde", latex: "\\tilde{▢}", display: "\\tilde{a}", tooltip: "Tilde" },
  { label: "overline", latex: "\\overline{▢}", display: "\\overline{AB}", tooltip: "Overline" },
  { label: "bold", latex: "\\mathbf{▢}", display: "\\mathbf{A}", tooltip: "Bold" },
  { label: "bb", latex: "\\mathbb{▢}", display: "\\mathbb{R}", tooltip: "Blackboard bold" },
  { label: "cal", latex: "\\mathcal{▢}", display: "\\mathcal{L}", tooltip: "Calligraphic" },
];

const TAB_ORDER: { key: string; label: string }[] = [
  { key: "recent", label: "Recent" },
  { key: "math", label: "Structures" },
  { key: "accents", label: "Accents" },
  { key: "greek", label: "Greek" },
  { key: "symbols", label: "Symbols" },
  { key: "operators", label: "Functions" },
  { key: "physics", label: "Physics" },
  { key: "chemistry", label: "Chemistry" },
];

const RECENT_KEY = "lilia-formula-recent";
const MAX_RECENT = 12;

function getRecentSymbols(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch { return []; }
}

function addRecent(latex: string) {
  const recent = getRecentSymbols().filter((s) => s !== latex);
  recent.unshift(latex);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

// Render a small KaTeX preview for a symbol button
function KaTeXButton({ latex, display, tooltip, onClick, size = "normal" }: {
  latex: string; display?: string; tooltip: string; onClick: () => void; size?: "normal" | "large";
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const rendered = useRef(false);

  useEffect(() => {
    if (!ref.current || rendered.current) return;
    rendered.current = true;
    const span = ref.current.querySelector(".katex-render");
    if (span) {
      try {
        span.innerHTML = renderLatex(display || latex.replace(/▢/g, "\\square"), {
          displayMode: false,
          throwOnError: false,
        });
      } catch {
        span.textContent = tooltip;
      }
    }
  }, [latex, display, tooltip]);

  return (
    <button
      ref={ref}
      data-testid={testIds.symbols.button}
      data-latex={latex}
      className={`
        flex items-center justify-center rounded-lg border border-[var(--lilia-border,#e2e8f0)]
        bg-[var(--lilia-bg,#fff)] cursor-pointer transition-all duration-100
        hover:border-[var(--lilia-primary,#0A76A4)] hover:bg-[var(--lilia-primary,#0A76A4)]/5
        hover:shadow-sm active:scale-95
        ${size === "large" ? "min-w-[48px] h-11 px-2" : "min-w-[40px] h-9 px-1.5"}
      `}
      onClick={onClick}
      title={tooltip}
    >
      <span className="katex-render [&_.katex]:text-[14px] [&_.katex]:font-bold" />
    </button>
  );
}

export function FormulaSymbolToolbar({ onInsert, query }: FormulaSymbolToolbarProps) {
  const [activeTab, setActiveTab] = useState("math");
  const [searchQuery, setSearchQuery] = useState(query?.text ?? "");
  useEffect(() => {
    if (query) setSearchQuery(query.text);
  }, [query]);
  const [recentSymbols, setRecentSymbols] = useState(getRecentSymbols);
  const tabsRef = useRef<HTMLDivElement>(null);

  const handleInsert = useCallback((latex: string) => {
    onInsert(latex);
    addRecent(latex);
    setRecentSymbols(getRecentSymbols());
  }, [onInsert]);

  const filteredSymbols = useMemo(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return EQUATION_SYMBOLS.filter(
        (s) =>
          s.label.toLowerCase().includes(query) ||
          s.latex.toLowerCase().includes(query) ||
          s.tooltip?.toLowerCase().includes(query)
      );
    }
    return [];
  }, [searchQuery]);

  const currentSymbols = useMemo((): SymbolButton[] => {
    if (activeTab === "recent") return [];
    if (activeTab === "math" || activeTab === "accents") return [];
    return getSymbolsByCategory(activeTab as SymbolCategory);
  }, [activeTab]);

  // Swipeable tabs on mobile
  const handleTabScroll = useCallback((direction: "left" | "right") => {
    if (!tabsRef.current) return;
    tabsRef.current.scrollBy({ left: direction === "right" ? 120 : -120, behavior: "smooth" });
  }, []);

  return (
    <div className="flex flex-col border-b border-[var(--lilia-border)] bg-[var(--lilia-surface,#f8fafc)] shrink-0">

      {/* Category tabs — horizontally scrollable */}
      <div className="relative">
        <div
          ref={tabsRef}
          className="flex items-center gap-0 px-1 overflow-x-auto scrollbar-none border-b border-[var(--lilia-border)]"
        >
          {TAB_ORDER.map((tab) => (
            <button
              key={tab.key}
              className={`
                px-3 py-2 text-[11px] font-medium whitespace-nowrap border-b-2 transition-colors shrink-0
                ${activeTab === tab.key && !searchQuery
                  ? "text-[var(--lilia-primary,#0A76A4)] border-[var(--lilia-primary,#0A76A4)]"
                  : "text-[var(--lilia-text-muted,#94a3b8)] border-transparent hover:text-[var(--lilia-text,#334155)]"
                }
              `}
              data-testid={testIds.symbols.tab}
              data-tab={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearchQuery(""); }}
            >
              {tab.label}
            </button>
          ))}
          {/* Search */}
          <div className="relative ml-auto shrink-0 pr-1">
            <input
              type="text"
              data-testid={testIds.symbols.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-28 sm:w-36 px-2 py-1 text-[11px] border border-[var(--lilia-border)] rounded-md bg-[var(--lilia-bg)] text-[var(--lilia-text)] outline-none focus:border-[var(--lilia-primary)]"
            />
            {searchQuery && (
              <button
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)] text-sm"
                onClick={() => setSearchQuery("")}
              >&times;</button>
            )}
          </div>
        </div>
      </div>

      {/* Symbol grid */}
      <div className="max-h-40 sm:max-h-48 overflow-y-auto p-2">

        {/* Search results */}
        {searchQuery.trim() && (
          <div className="flex flex-wrap gap-1.5">
            {filteredSymbols.map((s, i) => (
              <KaTeXButton key={`${s.latex}-${i}`} latex={s.latex} tooltip={s.tooltip || s.label} onClick={() => handleInsert(s.latex)} />
            ))}
            {filteredSymbols.length === 0 && (() => {
              const empty = symbolsEmptyState(searchQuery, EQUATION_SYMBOLS.length);
              return (
                <div data-testid={testIds.symbols.empty} className="w-full px-1 py-3 text-xs text-[var(--lilia-text-muted)] space-y-1">
                  <div className="font-medium text-[var(--lilia-text)]">{empty.headline}</div>
                  {empty.codepoint && <div className="font-mono">{empty.codepoint}</div>}
                  <div>{empty.catalogLine}</div>
                  {empty.routes.map((r) => <div key={r}>{r}</div>)}
                </div>
              );
            })()}
          </div>
        )}

        {/* Recent tab */}
        {!searchQuery && activeTab === "recent" && (
          <div className="flex flex-wrap gap-1.5">
            {recentSymbols.length > 0 ? recentSymbols.map((latex, i) => (
              <KaTeXButton key={`recent-${i}`} latex={latex} tooltip={latex} onClick={() => handleInsert(latex)} />
            )) : (
              <div className="w-full text-center text-xs text-[var(--lilia-text-muted)] italic py-4">
                Recently used symbols will appear here
              </div>
            )}
          </div>
        )}

        {/* Structures tab */}
        {!searchQuery && activeTab === "math" && (
          <div className="space-y-2">
            <div className="text-[9px] uppercase tracking-wider text-[var(--lilia-text-muted)] font-semibold px-0.5">Templates</div>
            <div className="flex flex-wrap gap-1.5">
              {STRUCTURES.map((s, i) => (
                <KaTeXButton key={`struct-${i}`} latex={s.latex} display={s.display} tooltip={s.tooltip} onClick={() => handleInsert(s.latex)} size="large" />
              ))}
            </div>
          </div>
        )}

        {/* Accents tab */}
        {!searchQuery && activeTab === "accents" && (
          <div className="space-y-2">
            <div className="text-[9px] uppercase tracking-wider text-[var(--lilia-text-muted)] font-semibold px-0.5">Accents & Decorations</div>
            <div className="flex flex-wrap gap-1.5">
              {ACCENTS.map((s, i) => (
                <KaTeXButton key={`accent-${i}`} latex={s.latex} display={s.display} tooltip={s.tooltip} onClick={() => handleInsert(s.latex)} size="large" />
              ))}
            </div>
          </div>
        )}

        {/* Regular category symbols */}
        {!searchQuery && !["recent", "math", "accents"].includes(activeTab) && (
          <div className="flex flex-wrap gap-1.5">
            {currentSymbols.map((s, i) => (
              <KaTeXButton key={`${s.latex}-${i}`} latex={s.latex} tooltip={s.tooltip || s.label} onClick={() => handleInsert(s.latex)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
